import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  TextField,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateField } from '@mui/x-date-pickers/DateField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createGuideTourMedicalAppointmentList,
  guideTourGeneralCustomerList,
} from '../api';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import { useAuth } from '../hooks/useAuth';
import { IGeneralCustomerList } from '../utils/types';
import { IGuideTourHealthMedicineInfo } from '../pages/GuidanceTourHealthMedicinePage';

interface AddEditGuidanceTourHealthMedicineAndDialysisReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  guideSalesIndividualId: string;
  dialysisCustomerList: IGuideTourHealthMedicineInfo[];
}

export function AddEditGuidanceTourHealthMedicalAppointmentDialog({
  open,
  onClose,
  onSave,
  guideSalesIndividualId,
  dialysisCustomerList,
}: AddEditGuidanceTourHealthMedicineAndDialysisReportDialogProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    customer: { id: 0, fullName: '', salesIndividualId: '' },
    date: '',
    time: '',
    type: 'DIALYSIS',
    salesIndividualId: 0,
  });
  const [generalCustomerList, setGeneralCustomerList] = useState<
    IGeneralCustomerList[]
  >([]);
  const showSnackbar = useAlertSnackbar();

  const handleInputChange =
    (field: string) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFormData({ ...formData, [field]: event.target.value });
    };

  useEffect(() => {
    fetchGuideTourGeneralCustomerList(guideSalesIndividualId);
  }, [guideSalesIndividualId]);

  const fetchGuideTourGeneralCustomerList = async (
    guideSalesIndividualId: string,
  ) => {
    try {
      const res = await guideTourGeneralCustomerList(
        guideSalesIndividualId,
        token,
      );
      const data = await res.json();
      if (res?.status === 200) {
        setGeneralCustomerList(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAddEditMedicalAppointmentList = async () => {
    try {
      setLoading(true);

      const res = await createGuideTourMedicalAppointmentList(
        guideSalesIndividualId,
        token,
        formData.date,
        formData.time,
        formData.type,
        formData.salesIndividualId,
      );

      if (res?.status !== 200 && res?.status !== 201) {
        let errorMessage = `Request failed with status ${res.status}`;

        try {
          const errorResponse = await res.json();
          errorMessage = errorResponse.detail || errorMessage;
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
        }

        throw new Error(errorMessage);
      }
    } catch (e: any) {
      const errorMessage = e.message || 'Beklenmeyen bir hata oluştu.';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    await fetchAddEditMedicalAppointmentList();
    onSave();
    handleClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      customer: { id: 0, fullName: '', salesIndividualId: '' },
      date: '',
      time: '',
      type: 'DIALYSIS',
      salesIndividualId: 0,
    });
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('guidanceTourHealth.dialysisReportDialog.medicalAppointment')}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth>
          <Autocomplete
            options={dialysisCustomerList}
            getOptionLabel={(option) =>
              `${option.customerFirstName} ${option.customerLastName}` || ''
            }
            value={dialysisCustomerList.find(
              (customer) =>
                customer.salesIndividualId === formData.salesIndividualId,
            )}
            onChange={(e, newValue) => {
              setFormData((prev) => ({
                ...prev,
                salesIndividualId: newValue?.salesIndividualId || 0,
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('guidanceTourHealth.disabilityDialog.customerName')}
                size="small"
              />
            )}
          />
        </FormControl>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {!isMobile ? (
            <DatePicker
              value={
                formData.date
                  ? dayjs(formData.date)
                      .set('hour', 12)
                      .set('minute', 0)
                      .set('second', 0)
                      .toDate()
                  : null
              }
              onChange={(e) => {
                const adjustedDate = dayjs(e)
                  .set('hour', 12)
                  .set('minute', 0)
                  .set('second', 0)
                  .toDate()
                  .toISOString();
                setFormData((prevData) => ({
                  ...prevData,
                  date: adjustedDate,
                }));
              }}
              sx={{
                width: '100%',
                backgroundColor: '#FAFAFA',
              }}
              slotProps={{ textField: { size: 'small' } }}
              format="dd/MM/yyyy"
            />
          ) : (
            <DateField
              value={
                formData.date
                  ? dayjs(formData.date)
                      .set('hour', 12)
                      .set('minute', 0)
                      .set('second', 0)
                      .toDate()
                  : null
              }
              onChange={(e) => {
                const adjustedDate = dayjs(e)
                  .set('hour', 12)
                  .set('minute', 0)
                  .set('second', 0)
                  .toDate()
                  .toISOString();
                setFormData((prevData) => ({
                  ...prevData,
                  date: adjustedDate,
                }));
              }}
              sx={{
                width: '100%',
                backgroundColor: '#FAFAFA',
              }}
              size="small"
              format="dd/MM/yyyy"
            />
          )}
          <TextField
            label={t('guidanceTourHealth.dialysisReportDialog.time')}
            type="time"
            value={formData.time}
            onChange={handleInputChange('time')}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              step: 300,
              pattern: '[0-2][0-9]:[0-5][0-9]',
            }}
            fullWidth
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          {t('accountInfosPage.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('accountInfosPage.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
